import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createContentTreeWalker } from '../utils/contentTextNodes';

/**
 * Find the text node and offset for a given character position in a container.
 */
export function findNodeAtCharOffset(container, targetOffset) {
  const walker = createContentTreeWalker(container);
  let charCount = 0;

  while (walker.nextNode()) {
    const nodeLength = walker.currentNode.textContent?.length || 0;
    if (charCount + nodeLength >= targetOffset) {
      return {
        node: walker.currentNode,
        offset: targetOffset - charCount,
      };
    }
    charCount += nodeLength;
  }

  // Fallback to last node
  if (walker.currentNode) {
    return {
      node: walker.currentNode,
      offset: walker.currentNode.textContent?.length || 0,
    };
  }
  return null;
}

/**
 * Calculate character offset from container start to a specific node/offset.
 */
export function getCharOffset(container, targetNode, targetOffset) {
  const walker = createContentTreeWalker(container);
  let charCount = 0;

  while (walker.nextNode()) {
    if (walker.currentNode === targetNode) {
      return charCount + targetOffset;
    }
    charCount += walker.currentNode.textContent?.length || 0;
  }
  return charCount;
}

/**
 * Calculate highlight overlay rectangles from a Range, relative to container.
 */
function getRangeRects(range, container) {
  if (!range || !container) return null;

  try {
    const containerRect = container.getBoundingClientRect();
    const clientRects = range.getClientRects();
    const rects = [];

    for (let i = 0; i < clientRects.length; i++) {
      const rect = clientRects[i];
      // Skip tiny rects (whitespace)
      if (rect.width < 2 || rect.height < 2) continue;

      rects.push({
        top: rect.top - containerRect.top + container.scrollTop,
        left: rect.left - containerRect.left + container.scrollLeft,
        width: rect.width,
        height: rect.height,
      });
    }

    return rects.length > 0 ? rects : null;
  } catch {
    return null;
  }
}

/**
 * Create a Range from character offsets within a container.
 */
function createRangeFromOffsets(container, startOffset, endOffset) {
  if (!container) return null;

  try {
    const startPos = findNodeAtCharOffset(container, startOffset);
    const endPos = findNodeAtCharOffset(container, endOffset);
    if (!startPos || !endPos) return null;

    const range = document.createRange();
    range.setStart(startPos.node, startPos.offset);
    range.setEnd(endPos.node, endPos.offset);
    return range;
  } catch {
    return null;
  }
}

/**
 * Hook for text selection with persistent highlighting.
 *
 * Handles two content types differently:
 * - HTML (dangerouslySetInnerHTML): DOM nodes replaced on render, uses char offsets
 * - Markdown (React components): DOM nodes preserved, uses saved Range
 */
export function useTextSelection(containerRef, options = {}) {
  const { minLength = 10, maxLength = 2000, contentType = null } = options;

  const [selection, setSelection] = useState(null);
  const [highlightRects, setHighlightRects] = useState(null);

  const isLockedRef = useRef(false);
  const savedRangeRef = useRef(null);
  // Tracks whether a mouse button / finger is currently down. Used to keep
  // the popup suppressed mid-drag so it only appears after release.
  const isPointerDownRef = useRef(false);

  const isHtmlContent = contentType?.toUpperCase() === 'HTML';

  // Ref for debounce timeout (declared early for use in clearSelection)
  const selectionChangeTimeoutRef = useRef(null);

  // Clear everything
  const clearSelection = useCallback(() => {
    setSelection(null);
    setHighlightRects(null);
    savedRangeRef.current = null;
    isLockedRef.current = false;
    // Clear any pending selection processing
    if (selectionChangeTimeoutRef.current) {
      clearTimeout(selectionChangeTimeoutRef.current);
      selectionChangeTimeoutRef.current = null;
    }
    try {
      window.getSelection()?.removeAllRanges();
    } catch {
      // Ignore
    }
  }, []);

  // Core selection processing logic - shared between mouse and touch events
  const processSelection = useCallback(() => {
    // If already locked (popup showing), ignore
    if (isLockedRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;

    const text = sel.toString().trim();
    if (text.length < minLength || text.length > maxLength) return;

    // Check if selection is within our container
    let range;
    try {
      range = sel.getRangeAt(0);
    } catch {
      return;
    }

    if (!container.contains(range.commonAncestorContainer)) return;

    // Save range for Markdown (where DOM persists)
    savedRangeRef.current = range.cloneRange();

    // Calculate character offsets for HTML (where DOM gets replaced)
    const startOffset = getCharOffset(container, range.startContainer, range.startOffset);
    const endOffset = getCharOffset(container, range.endContainer, range.endOffset);

    // Get selection rect in viewport coordinates (for position: fixed popup)
    const rect = range.getBoundingClientRect();
    const position = {
      // Store both top and bottom so popup can choose above/below
      selectionTop: rect.top,
      selectionBottom: rect.bottom,
      // Center horizontally on the selection
      left: rect.left + (rect.width / 2),
      // Selection dimensions for reference
      width: rect.width,
      height: rect.height,
    };

    // Calculate initial highlight rects
    const rects = getRangeRects(range, container);

    // Set state
    setHighlightRects(rects);
    setSelection({ text, startOffset, endOffset, position });
    isLockedRef.current = true;

    // Clear native selection - we use our own overlay for highlighting
    try {
      window.getSelection()?.removeAllRanges();
    } catch {
      // Ignore
    }
  }, [containerRef, minLength, maxLength]);

  // Handle pointer release (mouseup / touchend). This is the *only* path
  // that triggers the popup on desktop — selectionchange is gated below.
  const handlePointerUp = useCallback(() => {
    isPointerDownRef.current = false;
    processSelection();
  }, [processSelection]);

  // Handle selectionchange for iOS/touch devices.
  // iOS Safari uses long-press for text selection, which fires
  // selectionchange. On desktop, selectionchange also fires continuously
  // mid-drag, so we bail until the pointer is released — that way the
  // popup never appears on a debounce timer mid-selection.
  const handleSelectionChange = useCallback(() => {
    // Debounce to avoid processing during active selection
    if (selectionChangeTimeoutRef.current) {
      clearTimeout(selectionChangeTimeoutRef.current);
    }

    selectionChangeTimeoutRef.current = setTimeout(() => {
      // Don't open the popup until the user has let go.
      if (isPointerDownRef.current) return;
      const sel = window.getSelection();
      // Only process if there's actual selected text (not collapsed)
      if (sel && !sel.isCollapsed && sel.toString().trim().length > 0) {
        processSelection();
      }
    }, 300);
  }, [processSelection]);

  // Handle mousedown/touchstart: clear any open popup and mark pointer down
  const handlePointerDown = useCallback((e) => {
    // Don't clear if clicking/tapping on popup
    if (e.target.closest('.ask-graspd-popup')) return;

    isPointerDownRef.current = true;
    if (isLockedRef.current) {
      clearSelection();
    }
  }, [clearSelection]);

  // Set up document-level listeners
  useEffect(() => {
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('touchend', handlePointerUp);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    // iOS Safari fires selectionchange instead of mouseup for text selection
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchend', handlePointerUp);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('selectionchange', handleSelectionChange);
      // Clean up debounce timeout
      if (selectionChangeTimeoutRef.current) {
        clearTimeout(selectionChangeTimeoutRef.current);
      }
    };
  }, [handlePointerUp, handlePointerDown, handleSelectionChange]);

  // Recalculate highlight rects after render (handles DOM replacement)
  useLayoutEffect(() => {
    if (!isLockedRef.current || !selection) return;

    const container = containerRef.current;
    if (!container) return;

    let range;

    if (isHtmlContent) {
      // HTML: recreate range from character offsets (DOM was replaced)
      range = createRangeFromOffsets(container, selection.startOffset, selection.endOffset);
    } else {
      // Markdown: use saved range (DOM is preserved by React)
      range = savedRangeRef.current;
    }

    if (range) {
      const rects = getRangeRects(range, container);
      if (rects) {
        setHighlightRects(rects);
      }
    }
  }, [selection, isHtmlContent, containerRef]);

  return { selection, clearSelection, highlightRects };
}
